import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from '@/components';
import { EngineService } from '@/services/engine';
import { CACHE_KEYS } from '@/constants/cache-keys';
import { I18nNamespace } from '@/lib/i18n-namespaces';
import { revalidateByCacheKeyPrefix } from '@/utils/swr';
import type { UpdateVehiclePayload, Vehicle } from '@/types';

interface UseVehicleActionsParams {
    onVehicleUpdated: (updatedFields: Partial<Vehicle>) => void;
    onVehicleDeleted: () => void;
    /**
     * Called when updating a vehicle fails with a duplicate-license-plate
     * conflict (HTTP 409). Return true when the conflict was handled (e.g. the
     * merge dialog was opened) to suppress the generic error toast.
     */
    onDuplicatePlateConflict?: (error: unknown, vehicleId: string) => boolean;
}

export function useVehicleActions({
    onVehicleUpdated,
    onVehicleDeleted,
    onDuplicatePlateConflict,
}: UseVehicleActionsParams) {
    const { t } = useTranslation(I18nNamespace.DIRECTORY);
    const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    const [isEditingVehicle, setIsEditingVehicle] = useState(false);
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const [isDeletingVehicle, setIsDeletingVehicle] = useState(false);

    const editVehicle = async (
        vehicleId: string,
        payload: UpdateVehiclePayload,
    ): Promise<boolean> => {
        setIsEditingVehicle(true);
        try {
            const updated = await EngineService.updateVehicle(
                vehicleId,
                payload,
            );
            onVehicleUpdated(updated);
            revalidateByCacheKeyPrefix(CACHE_KEYS.ALL_VEHICLES);
            toast.success(t('vehicles.editModal.success'));
            setIsEditModalOpen(false);
            return true;
        } catch (error) {
            if (onDuplicatePlateConflict?.(error, vehicleId)) {
                setIsEditModalOpen(false);
                return false;
            }
            toast.error(t('vehicles.editModal.error'));
            return false;
        } finally {
            setIsEditingVehicle(false);
        }
    };

    const deleteVehicle = async (vehicleId: string) => {
        setIsDeletingVehicle(true);
        try {
            await EngineService.deleteVehicle(vehicleId);
            revalidateByCacheKeyPrefix(CACHE_KEYS.ALL_VEHICLES);
            toast.success(t('vehicles.deleteSuccess', { count: 1 }));
            setIsDeleteModalOpen(false);
            onVehicleDeleted();
        } catch {
            toast.error(t('vehicles.deleteError'));
        } finally {
            setIsDeletingVehicle(false);
        }
    };

    const markAsUnknown = async (vehicleId: string) => {
        onVehicleUpdated({ name: null, tags: [], known: false });
        try {
            await EngineService.updateVehicle(vehicleId, {
                name: null,
                tags: [],
            });
            revalidateByCacheKeyPrefix(CACHE_KEYS.ALL_VEHICLES);
            toast.success(t('vehicles.markAsUnknown.success'));
        } catch {
            onVehicleUpdated({ known: true });
            toast.error(t('vehicles.markAsUnknown.error'));
        }
    };

    return {
        isEditModalOpen,
        setIsEditModalOpen,
        isEditingVehicle,
        editVehicle,
        markAsUnknown,
        isDeleteModalOpen,
        setIsDeleteModalOpen,
        isDeletingVehicle,
        deleteVehicle,
    };
}
